// FILE: src/Manager/AssignForm.js
// --- CORRECTED VERSION ---

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import managerService from '../services/managerService';
import './AassignForm.css';

// ====================================================================
// SECTION 1: CUSTOM HOOK LOGIC (Manages all state and API calls)
// ====================================================================
const useFormManagement = () => {
    const [assignedForms, setAssignedForms] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toasts, setToasts] = useState([]);

    const addToast = (message, type = 'error') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    };
    
    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await managerService.getPageData(); 
            setAssignedForms(data.assignedForms || []);
            setBrokers(data.brokers?.filter(b => b.isActive) || []);
        } catch (err) {
            addToast('Failed to load page data. Please refresh.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const handleApiCall = async (apiCall, successMessage) => {
        setIsSubmitting(true);
        try {
            await apiCall();
            addToast(successMessage, 'success');
            // Re-fetch data after a successful call
            const data = await managerService.getPageData();
            setAssignedForms(data.assignedForms || []);
        } catch (err) {
            addToast(err.response?.data?.message || 'An unexpected error occurred.');
            throw err;
        } finally {
            setIsSubmitting(false);
        }
    };

    // <<< FIX 1: Accepts a single formData object. This is cleaner.
    const assignForm = (formData) => {
        // <<< FIX 2: Call the service with the correctly structured object.
        return handleApiCall(
            () => managerService.assignForm(formData),
            'Form assigned successfully!'
        );
    };

    const updateForm = (formId, updatedData) => {
         return handleApiCall(
            () => managerService.updateAssignedForm(formId, updatedData),
            'Form updated successfully!'
        );
    };

    const deleteForm = (formId) => {
         return handleApiCall(
            () => managerService.deleteAssignedForm(formId),
            'Form deleted successfully!'
        );
    };

    return {
        assignedForms, brokers, loading, isSubmitting, toasts,
        removeToast, assignForm, updateForm, deleteForm
    };
};

// ====================================================================
// SECTION 2: UI COMPONENTS (Modals, Toasts, etc.)
// ====================================================================

const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => onClose(), 5000);
        return () => clearTimeout(timer);
    }, [onClose]);
    if (!message) return null;
    const toastStyle = { minWidth: '300px', padding: '1rem 1.5rem', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', backgroundColor: type === 'success' ? '#28a745' : '#dc3545', marginBottom: '10px' };
    const buttonStyle = { background: 'none', border: 'none', color: 'inherit', fontSize: '1.5rem', lineHeight: '1', cursor: 'pointer', opacity: '0.8', paddingLeft: '1rem' };
    return <div style={toastStyle}><span>{message}</span><button style={buttonStyle} onClick={onClose}>×</button></div>;
};
const ToastContainer = ({ toasts, removeToast }) => {
    const containerStyle = { position: 'fixed', top: '20px', right: '20px', zIndex: '9999' };
    return <div style={containerStyle}>{toasts.map(toast => <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />)}</div>;
};

const AssignFormModal = ({ isOpen, onClose, onSave, brokers, isSubmitting }) => {
    const [brokerId, setBrokerId] = useState('');
    const [lotNumber, setLotNumber] = useState('');
    const [formType, setFormType] = useState('Structural Options'); 
    const [expiresAt, setExpiresAt] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (brokers.length > 0) setBrokerId(brokers[0]._id);
            setLotNumber('');
            setFormType('Structural Options');
            setExpiresAt('');
        }
    }, [isOpen, brokers]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!brokerId || !lotNumber || !formType || !expiresAt) {
            alert('Please select a broker and fill out all fields, including expiry date.');
            return;
        }
        // <<< FIX 3: Pass a single, correctly named object to the onSave handler.
        onSave({ 
            brokerUserId: brokerId, // The server expects 'brokerUserId'
            lotNumber, 
            formType, 
            expiresAt 
        });
    };
     return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <form onSubmit={handleSubmit}>
                        <h2>Assign New Form</h2>
                        <div className="form-group">
                            <label>Assign To Broker</label>
                            <select value={brokerId} onChange={(e) => setBrokerId(e.target.value)} required>
                                <option value="" disabled>Select a broker...</option>
                                {brokers.map(b => <option key={b._id} value={b._id}>{b.name} ({b.email})</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Lot Number</label>
                            <input type="text" placeholder="e.g., 14" value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} required />
                        </div>
                        <div className="form-group">
                            <label>Form Type</label>
                            <select value={formType} onChange={(e) => setFormType(e.target.value)} required>
                                <option value="Structural Options">Structural Options</option>
                                <option value="Design Options">Design Options</option>
                                <option value="Pre-Construction">Pre-Construction</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Expiry Date</label>
                            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} required />
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                            <button type="submit" className="btn-save" disabled={isSubmitting}>
                                {isSubmitting ? 'Assigning...' : 'Assign Form'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };
    
    const EditFormModal = ({ isOpen, onClose, onSave, formToEdit, isSubmitting }) => {
        const [expiresAt, setExpiresAt] = useState('');
    
    
        useEffect(() => {
            if (formToEdit) {
                setExpiresAt(formToEdit.expiresAt ? format(new Date(formToEdit.expiresAt), 'yyyy-MM-dd') : '');
            }
        }, [formToEdit]);
    
        if (!isOpen) return null;
    
        const handleSubmit = (e) => {
            e.preventDefault();
            onSave(formToEdit._id, { expiresAt });
        };
    
        const brokerName = formToEdit?.brokerUserId?.name || 'N/A';
        const brokerEmail = formToEdit?.brokerUserId?.email || 'N/A';
    
        return (
           <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <form onSubmit={handleSubmit}>
                        <h2>Edit Assigned Form (Invoice: {formToEdit.baseInvoiceId})</h2>
                        
                        <p style={{ marginTop: '-10px', marginBottom: '20px', fontSize: '0.9rem', color: '#666' }}>
                            NOTE: STATUS CAN ONLY BE CHANGED INSIDE THE FORM.
                        </p>
    
                        <div className="form-group">
                            <label>Broker</label>
                            <div className="static-info" style={{ padding: '10px', background: '#f4f4f4', borderRadius: '4px', border: '1px solid #ccc' }}>
                                {brokerName} ({brokerEmail})
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>Expiry Date</label>
                            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} required />
                        </div>
                        
                        <div className="modal-actions">
                            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
                            <button type="submit" className="btn-save" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };
    
    const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, isSubmitting }) => {
        if (!isOpen) return null;
        return <div className="modal-overlay" onClick={onClose}><div className="modal-content" onClick={e => e.stopPropagation()}><h2>Confirm Deletion</h2><p>Are you sure you want to permanently delete this assigned form?</p><div className="modal-actions"><button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button><button type="button" className="btn-delete" onClick={onConfirm} disabled={isSubmitting}>{isSubmitting ? 'Deleting...' : 'Delete'}</button></div></div></div>;
    };
    

const AassignForm = () => {
  const { assignedForms, brokers, loading, isSubmitting, toasts, removeToast, assignForm, updateForm, deleteForm } = useFormManagement();
     const [statusFilter, setStatusFilter] = useState('all');
     const [searchTerm, setSearchTerm] = useState('');
     const [modalState, setModalState] = useState({ type: null, data: null });
 
     // <<< FIX 4: Handle the single object from the modal.
     const handleAssignForm = (formData) => {
         assignForm(formData).then(() => setModalState({ type: null, data: null })).catch(() => {});
     };
     const handleUpdateForm = (formId, updatedData) => {
         updateForm(formId, updatedData).then(() => setModalState({ type: null, data: null })).catch(() => {});
     };
     const handleDeleteForm = () => {
         if (!modalState.data) return;
         deleteForm(modalState.data).then(() => setModalState({ type: null, data: null })).catch(() => {});
     };
     
     const { filteredForms, analysisData } = useMemo(() => {
         const analysisData = {
             total: assignedForms.length,
             pending: assignedForms.filter(f => f.status === 'pending').length,
             submitted: assignedForms.filter(f => f.status === 'submitted').length,
             reassigned: assignedForms.filter(f => f.status === 'reassigned').length,
             finalized: assignedForms.filter(f => f.status === 'completed').length,
         };
 
         let processedForms = [...assignedForms];
 
         if (statusFilter !== 'all') {
             const filterToApply = statusFilter === 'finalized' ? 'completed' : statusFilter;
             processedForms = processedForms.filter(form => form.status === filterToApply);
         }
 
         if (searchTerm) {
             const lowercasedTerm = searchTerm.toLowerCase();
             processedForms = processedForms.filter(form => 
                 (form.brokerUserId?.name?.toLowerCase() || '').includes(lowercasedTerm) ||
                 (form.brokerUserId?.email?.toLowerCase() || '').includes(lowercasedTerm) ||
                 (form.baseInvoiceId || '').toLowerCase().includes(lowercasedTerm) ||
                 (form.lotNumber || '').toLowerCase().includes(lowercasedTerm)
             );
         }
         
         processedForms.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
         
         return { filteredForms: processedForms, analysisData };
 
     }, [assignedForms, statusFilter, searchTerm]);
     
     const formatDate = (dateString) => dateString ? format(new Date(dateString), 'MMM dd, yyyy') : 'N/A';
 
     return (
         <div className="assign-forms-containers">
             <ToastContainer toasts={toasts} removeToast={removeToast} />
             <header className="page-header"><h1>Form Management</h1><button className="btn-add" onClick={() => setModalState({ type: 'assign', data: null })}>+ Assign New Form</button></header>
             
             <section className="analysis-cards">
                 <div className="card"><div className="card-content"><span className="card-value">{analysisData.total}</span><span className="card-label">Total Assigned</span></div></div>
                 <div className="card status-pending"><div className="card-content"><span className="card-value">{analysisData.pending}</span><span className="card-label">Pending</span></div></div>
                 <div className="card status-submitted"><div className="card-content"><span className="card-value">{analysisData.submitted || 0}</span><span className="card-label">Submitted</span></div></div>
                 <div className="card status-reassigned"><div className="card-content"><span className="card-value">{analysisData.reassigned || 0}</span><span className="card-label">Reassigned</span></div></div>
                 <div className="card status-completed"><div className="card-content"><span className="card-value">{analysisData.finalized || 0}</span><span className="card-label">Finalized</span></div></div>
             </section>
 
             <div className="data-table-card">
                 <div className="toolbar">
                     <input type="text" className="search-input" placeholder="Search by Invoice, Lot, Broker Name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                     <div className="filter-buttons">
                         {['All', 'Pending', 'Submitted', 'Reassigned', 'Finalized'].map(filter => <button key={filter} className={statusFilter === filter.toLowerCase() ? 'active' : ''} onClick={() => setStatusFilter(filter.toLowerCase())}>{filter}</button>)}
                     </div>
                 </div>
 
                 <table className="data-table">
                     <thead><tr><th>Invoice / Lot</th><th>Form Type</th><th>Assigned To</th><th>Assigned On</th><th>Status</th><th>Expiry Date</th><th>Actions</th></tr></thead>
                     <tbody>
                         {loading ? <tr><td colSpan="7" className="loading-state">Loading...</td></tr> : 
                         filteredForms.length > 0 ? filteredForms.map(form => (
                             <tr key={form._id}>
                                 <td>
                                     <div className="user-cell"><span className="user-name">{form.baseInvoiceId || 'N/A'}</span><span className="user-email">Lot: {form.lotNumber}</span></div>
                                 </td>
                                 <td>{form.formType}</td>
                                 <td>
                                     <div className="user-cell"><span className="user-name">{form.brokerUserId?.name || 'N/A'}</span><span className="user-email">{form.brokerUserId?.email || 'N/A'}</span></div>
                                 </td>
                                 <td>{formatDate(form.createdAt)}</td>
                                 <td><span className={`status-badge status-${form.status}`}>{form.status}</span></td>
                                 <td>{formatDate(form.expiresAt)}</td>
                                 <td className="actions-cell">
                                     <button className="btn btn-sm btn-outline-primary" onClick={() => setModalState({ type: 'edit', data: form })}>Edit</button>
                                     <button className="btn btn-sm btn-outline-danger" onClick={() => setModalState({ type: 'delete', data: form._id })}>Delete</button>
                                 </td>
                             </tr>
                         )) : <tr><td colSpan="7" className="empty-state">No forms found.</td></tr>}
                     </tbody>
                 </table>
             </div>
 
             <AssignFormModal isOpen={modalState.type === 'assign'} onClose={() => setModalState({ type: null, data: null })} onSave={handleAssignForm} brokers={brokers} isSubmitting={isSubmitting} />
             <EditFormModal isOpen={modalState.type === 'edit'} onClose={() => setModalState({ type: null, data: null })} onSave={handleUpdateForm} brokers={brokers} formToEdit={modalState.data} isSubmitting={isSubmitting} />
             <DeleteConfirmationModal isOpen={modalState.type === 'delete'} onClose={() => setModalState({ type: null, data: null })} onConfirm={handleDeleteForm} isSubmitting={isSubmitting} />
         </div>
        );
};

export default AassignForm